import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, RefreshCw, Search, Loader2, Check, X, Clock, AlertTriangle,
  ArrowUp, ArrowDown, Archive, ArchiveRestore, Inbox, Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-02-26 (Dave) — Admin → Email Log tile.
//
// Reads GET /api/admin/email-logs (see routes/admin_email_logs.py for
// the full scope story). Platform admins see every row in the last
// 30 days; Firm admins see only rows attributed to firms they
// administer. Postmark webhook updates the status column live —
// initial state is "sent", then flips to "delivered" or "bounced" or
// "spam_complaint" once Postmark reports back.

const STATUS_STYLE = {
  sent:            { bg: "#E9F1F9", fg: "#1C4E80", border: "#1C4E80", label: "Sent",           Icon: Clock },
  delivered:       { bg: "#EAF6EF", fg: "#2E7D5B", border: "#2E7D5B", label: "Delivered",      Icon: Check },
  received:        { bg: "#EAF1FB", fg: "#0091D5", border: "#0091D5", label: "Received",       Icon: Check },
  bounced:         { bg: "#FEF5F2", fg: "#EA6A47", border: "#EA6A47", label: "Bounced",        Icon: X },
  spam_complaint:  { bg: "#FEE2DA", fg: "#B4331A", border: "#B4331A", label: "Spam Complaint", Icon: AlertTriangle },
  failed:          { bg: "#FEF5F2", fg: "#EA6A47", border: "#EA6A47", label: "Failed",         Icon: X },
  dry_run:         { bg: "#F1F3F5", fg: "#5A6B75", border: "#B8C1CB", label: "Dry Run",        Icon: Clock },
};

const TYPE_STYLE = {
  invite:             { bg: "#F1F3F5", fg: "#1C4E80", label: "Invite" },
  password_reset:     { bg: "#F1F3F5", fg: "#1C4E80", label: "Password Reset" },
  deactivation:       { bg: "#F1F3F5", fg: "#5A6B75", label: "Deactivation" },
  support_ticket:     { bg: "#F1F3F5", fg: "#1C4E80", label: "Support Ticket" },
  support_autoreply:  { bg: "#F1F3F5", fg: "#5A6B75", label: "Support Auto-Reply" },
  platform_test:      { bg: "#F1F3F5", fg: "#5A6B75", label: "Platform Test" },
  inbound_reply:      { bg: "#EAF1FB", fg: "#0091D5", label: "Inbound Reply" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.sent;
  const { Icon } = s;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] tracking-[0.05em] font-semibold rounded-[3px]"
      style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
      data-testid={`email-log-status-${status}`}
    >
      <Icon size={9} strokeWidth={2.5} />
      {s.label}
    </span>
  );
}

function TypePill({ type }) {
  const s = TYPE_STYLE[type] || { bg: "#F1F3F5", fg: "#5A6B75", label: type };
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 text-[10.5px] font-medium rounded-[3px]"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`email-log-type-${type}`}
    >
      {s.label}
    </span>
  );
}

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    timeZone: "America/Chicago",
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export default function EmailLogScreen() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [windowDays, setWindowDays] = useState(30);
  const [canSeeAll, setCanSeeAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // 2026-02-28 (Dave) — Selection + archive.  `selected` is a Set
  // of email_log_ids; toggling the "select all visible" checkbox
  // adds/removes every currently-rendered row.  `view` toggles
  // between the Active inbox and the Archived trash.  `busy` blocks
  // duplicate clicks while a bulk request is in flight.
  const [selected, setSelected] = useState(() => new Set());
  const [view, setView] = useState("active"); // "active" | "archived"
  const [busy, setBusy] = useState(false);
  // 2026-09-13 (Dave) — Hard-delete confirm dialog. `pendingDelete`
  // holds either { kind: "bulk", ids: [...] } for the selection-bar
  // action or { kind: "row", id, subject } for a per-row trash click.
  const [pendingDelete, setPendingDelete] = useState(null);

  // 2026-02-27 (Dave) — Client-side sort. Default: most recent
  // Sent date first (matches the API's own ORDER BY sent_at DESC,
  // but making it explicit here lets the user re-sort other columns
  // and easily flip back with one click). Clicking an already-active
  // column toggles the direction.
  const [sortKey, setSortKey] = useState("sent_at");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "sent_at" ? "desc" : "asc"); }
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (view === "archived") params.only_archived = true;
      const r = await axios.get(`${API}/admin/email-logs`, { params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setWindowDays(r.data.window_days || 30);
      setCanSeeAll(!!r.data.can_see_all);
      // Any rows that were selected but are no longer in the loaded
      // set (e.g. because we just archived them or switched view)
      // drop out of the Set to keep the counter honest.
      const nextIds = new Set((r.data.rows || []).map((row) => row.email_log_id));
      setSelected((prev) => new Set([...prev].filter((id) => nextIds.has(id))));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr((typeof detail === "string" && detail) || e.message || "Failed to load email log.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // 2026-02-28 (Dave) — auto-refetch when the Type or Status
  // dropdown changes. Previously the dropdown updated state
  // but the visible rows didn't reload until the user clicked
  // Refresh, making the filter feel broken. Search (`q`) stays
  // Enter/Refresh-driven to avoid a request per keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [typeFilter, statusFilter, view]);

  const summary = useMemo(() => {
    const counts = { sent: 0, delivered: 0, bounced: 0, spam_complaint: 0, failed: 0, dry_run: 0 };
    for (const r of rows) counts[r.status] = (counts[r.status] || 0) + 1;
    return counts;
  }, [rows]);

  // Sort accessors — each returns a comparable primitive. Falsy /
  // missing values sort to the "bottom" regardless of direction so
  // rows with a subject always cluster ahead of empty ones.
  const sortAccessors = {
    sent_at:    (r) => r.sent_at || "",
    from:       (r) => (r.from_email || "").toLowerCase(),
    recipient:  (r) => (r.direction === "inbound" ? "uReserve inbound" : (r.recipient || "")).toLowerCase(),
    subject:    (r) => (r.subject || "").toLowerCase(),
    type:       (r) => r.type || "",
    status:     (r) => r.status || "",
    firm:       (r) => (r.tenant_name || "").toLowerCase(),
    association:(r) => (r.association_name || "").toLowerCase(),
    by:         (r) => (r.triggered_by_name || r.triggered_by_email || "").toLowerCase(),
  };
  const sortedRows = useMemo(() => {
    const acc = sortAccessors[sortKey] || sortAccessors.sent_at;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      // Empty strings push to the bottom regardless of direction.
      if (va === "" && vb !== "") return 1;
      if (vb === "" && va !== "") return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);

  // ---- Selection + bulk archive/unarchive helpers -----------------
  // 2026-02-28 (Dave) — declared AFTER sortedRows so the
  // `allVisibleSelected` derivation and `toggleAllVisible` action
  // can reference it without a temporal-dead-zone reference error.
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected =
    sortedRows.length > 0 &&
    sortedRows.every((r) => selected.has(r.email_log_id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const r of sortedRows) next.delete(r.email_log_id);
      } else {
        for (const r of sortedRows) next.add(r.email_log_id);
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (kind /* "archive" | "unarchive" */) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/email-logs/${kind}`, {
        email_log_ids: ids,
      });
      const n = res.data?.updated ?? 0;
      toast.success(
        kind === "archive"
          ? `${n} ${n === 1 ? "email" : "emails"} archived.`
          : `${n} ${n === 1 ? "email" : "emails"} restored.`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || `Failed to ${kind}.`);
    } finally {
      setBusy(false);
    }
  };

  // 2026-09-13 (Dave) — Hard delete. Backend only accepts already-
  // archived rows, so the UI only exposes this in the Archived tab.
  const runDelete = async (ids) => {
    if (!ids || ids.length === 0) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/email-logs/delete`, {
        email_log_ids: ids,
      });
      const n = res.data?.updated ?? 0;
      toast.success(`${n} ${n === 1 ? "email" : "emails"} permanently deleted.`);
      setPendingDelete(null);
      // Any selected id that just vanished should drop from the Set.
      setSelected((prev) => {
        const idSet = new Set(ids);
        return new Set([...prev].filter((x) => !idSet.has(x)));
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to delete.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel-compact flex flex-col" data-testid="screen-email-log">
      <div
        className="section-title-sm flex items-baseline justify-between"
        style={{ borderBottomColor: "#EA6A47" }}
      >
        <span style={{ fontSize: 20, fontWeight: 700 }} className="inline-flex items-center gap-2">
          <Mail size={18} className="text-navy" strokeWidth={1.8} />
          Email Log
        </span>
        <span className="text-slate text-[11px] tracking-[0.06em]" data-testid="email-log-window">
          Last {windowDays} days · {total} email{total === 1 ? "" : "s"}
          {!canSeeAll && " · Firm scope"}
        </span>
      </div>

      {/* View toggle: Active inbox vs Archived trash. 2026-02-28 (Dave) */}
      <div className="mt-3 inline-flex border border-navy rounded-[3px] overflow-hidden" data-testid="email-log-view-toggle">
        <button
          type="button"
          onClick={() => setView("active")}
          className={`inline-flex items-center gap-1 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors ${
            view === "active"
              ? "bg-navy text-white"
              : "bg-white text-navy hover:bg-navy-wash"
          }`}
          data-testid="email-log-view-active"
        >
          <Inbox size={11} strokeWidth={2.2} />
          Active
        </button>
        <button
          type="button"
          onClick={() => setView("archived")}
          className={`inline-flex items-center gap-1 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors border-l border-navy ${
            view === "archived"
              ? "bg-navy text-white"
              : "bg-white text-navy hover:bg-navy-wash"
          }`}
          data-testid="email-log-view-archived"
        >
          <Archive size={11} strokeWidth={2.2} />
          Archived
        </button>
      </div>

      {/* Filter row */}
      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="email-log-filters">
        <div className="relative">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-slate"
            strokeWidth={2}
          />
          <input
            type="text"
            placeholder="Search recipient or subject…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            className="pl-6 pr-2 py-1 text-[11.5px] border border-rule rounded-[3px] focus:border-navy focus:outline-none"
            style={{ width: 240 }}
            data-testid="email-log-search"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-2 py-1 text-[11.5px] border border-rule rounded-[3px] focus:border-navy focus:outline-none"
          data-testid="email-log-type-filter"
        >
          <option value="">All types</option>
          <option value="invite">Invite</option>
          <option value="password_reset">Password Reset</option>
          <option value="deactivation">Deactivation</option>
          <option value="support_ticket">Support Ticket</option>
          <option value="support_autoreply">Support Auto-Reply</option>
          <option value="platform_test">Platform Test</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 text-[11.5px] border border-rule rounded-[3px] focus:border-navy focus:outline-none"
          data-testid="email-log-status-filter"
        >
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="bounced">Bounced</option>
          <option value="spam_complaint">Spam Complaint</option>
          <option value="failed">Failed</option>
          <option value="dry_run">Dry Run</option>
        </select>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11.5px] font-semibold border border-navy text-navy rounded-[3px] hover:bg-navy hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="email-log-refresh"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Refresh
        </button>

        {/* Status count chips (compact summary) */}
        <span className="text-[10.5px] text-slate flex items-center gap-1.5 ml-2">
          {Object.entries(summary)
            .filter(([, c]) => c > 0)
            .map(([status, c]) => (
              <span key={status} className="inline-flex items-center gap-0.5">
                <StatusPill status={status} />
                <span className="font-mono text-navy">×{c}</span>
              </span>
            ))}
        </span>
      </div>

      {err && (
        <div
          className="mt-3 border border-coral bg-[#FEF5F2] px-3 py-2 text-[12px] text-coral rounded-[3px]"
          data-testid="email-log-error"
        >
          {err}
        </div>
      )}

      {/* Bulk action bar — visible when at least one row is selected.
          2026-02-28 (Dave) — the Archive / Restore action reads from
          `view`, so the button flips label + icon between the two
          tabs.  "Clear" wipes the selection without touching state. */}
      {selected.size > 0 && (
        <div
          className="mt-3 flex items-center gap-3 px-3 py-2 bg-navy text-white rounded-[3px]"
          data-testid="email-log-selection-bar"
        >
          <span className="text-[11.5px] font-semibold tracking-[0.06em]">
            {selected.size} selected
          </span>
          <button
            type="button"
            onClick={() => runBulk(view === "archived" ? "unarchive" : "archive")}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1 text-[11.5px] font-semibold bg-white text-navy border border-white rounded-[3px] hover:bg-navy-wash disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="email-log-bulk-action"
          >
            {busy ? (
              <Loader2 size={11} className="animate-spin" />
            ) : view === "archived" ? (
              <ArchiveRestore size={11} strokeWidth={2.2} />
            ) : (
              <Archive size={11} strokeWidth={2.2} />
            )}
            {view === "archived" ? "Restore" : "Archive"}
          </button>
          {view === "archived" && (
            <button
              type="button"
              onClick={() =>
                setPendingDelete({ kind: "bulk", ids: [...selected] })
              }
              disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-1 text-[11.5px] font-semibold bg-coral text-white border border-coral rounded-[3px] hover:bg-[#c9552d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="email-log-bulk-delete"
            >
              <Trash2 size={11} strokeWidth={2.2} />
              Delete Permanently
            </button>
          )}
          <button
            type="button"
            onClick={clearSelection}
            className="inline-flex items-center gap-1 px-3 py-1 text-[11.5px] font-semibold border border-white text-white rounded-[3px] hover:bg-[#164066] transition-colors"
            data-testid="email-log-bulk-clear"
          >
            <X size={11} strokeWidth={2.2} />
            Clear
          </button>
        </div>
      )}

      <div className="mt-3 overflow-auto flex-1 border border-rule rounded-[3px]" data-testid="email-log-table-wrap">
        <table className="u-table u-table-dense" style={{ tableLayout: "auto", width: "100%" }} data-testid="email-log-table">
          <thead>
            <tr style={{ background: "#1C4E80", color: "#fff" }}>
              {/* 2026-02-28 (Dave) — Select-all-visible checkbox
                  header column.  Clicking flips every currently-
                  rendered row into or out of the `selected` Set. */}
              <th
                style={{
                  padding: "6px 10px", textAlign: "center", width: 32,
                  cursor: "pointer", userSelect: "none",
                }}
                data-testid="email-log-th-select"
                onClick={toggleAllVisible}
              >
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Select all visible"
                  className="accent-white cursor-pointer"
                  data-testid="email-log-select-all"
                />
              </th>
              {[
                { label: "Date",       key: "sent_at",     width: 130 },
                { label: "From",       key: "from",        width: 200 },
                { label: "Recipient",  key: "recipient",   width: 200 },
                { label: "Subject",    key: "subject",     width: 260 },
                { label: "Type",       key: "type",        width: 120 },
                { label: "Status",     key: "status",      width: 130 },
                { label: "Firm",       key: "firm",        width: 150 },
                { label: "Association",key: "association", width: 160 },
                { label: "By",         key: "by",          width: 170 },
              ].map((c) => {
                const active = c.key === sortKey;
                const Arrow = active && sortDir === "asc" ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={c.label}
                    onClick={() => toggleSort(c.key)}
                    data-testid={`email-log-th-${c.key}`}
                    aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                    style={{
                      padding: "6px 10px", textAlign: "left", fontSize: 11,
                      letterSpacing: 0.8, textTransform: "uppercase", width: c.width,
                      cursor: "pointer", userSelect: "none",
                    }}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {/* 2026-02-27 (Dave) — sort arrow. Active column
                          shows a solid arrow (dir-dependent). Inactive
                          columns show a low-opacity down-arrow as a
                          discoverability hint so the CPA sees every
                          column is sortable, not just the active one. */}
                      <Arrow
                        size={11}
                        strokeWidth={2.4}
                        style={{ opacity: active ? 1 : 0.35 }}
                      />
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate text-[12px]">
                  <Loader2 size={14} className="inline-block animate-spin mr-1.5 -mb-0.5" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && !err && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate italic text-[12px]" data-testid="email-log-empty">
                  {view === "archived"
                    ? "No archived emails."
                    : `No emails in the last ${windowDays} days.`}
                </td>
              </tr>
            )}
            {sortedRows.map((row, i) => {
              // 2026-02-26 (Dave) — indent threaded inbound replies
              // ~14 px so a reply visually nests under its outbound
              // parent. Left-border in sky signals "this is a reply."
              const isInbound = row.direction === "inbound";
              const isReply = isInbound && !!row.in_reply_to_email_log_id;
              const isSelected = selected.has(row.email_log_id);
              return (
              <tr
                key={row.email_log_id}
                data-testid={`email-log-row-${row.email_log_id}`}
                style={{
                  background: isSelected
                    ? "#E9F1F9"
                    : isInbound
                      ? "#F7FBFF"
                      : (i % 2 === 1 ? "#FAFAF9" : "#fff"),
                }}
              >
                {/* Per-row select checkbox — clicking the cell (not
                    just the box) toggles selection so it's forgiving
                    on smaller pointers. */}
                <td
                  className="px-2.5 py-1.5 text-center cursor-pointer"
                  onClick={() => toggleRow(row.email_log_id)}
                  data-testid={`email-log-select-${row.email_log_id}`}
                >
                  <div className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRow(row.email_log_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-navy cursor-pointer"
                      aria-label="Select row"
                    />
                    {view === "archived" && (
                      <button
                        type="button"
                        onClick={(e) => {
                          // Don't toggle the row's checkbox when
                          // clicking the trash icon.
                          e.stopPropagation();
                          setPendingDelete({
                            kind: "row",
                            id: row.email_log_id,
                            subject: row.subject || row.recipient || "this email",
                          });
                        }}
                        className="text-slate hover:text-coral transition-colors"
                        aria-label="Delete permanently"
                        title="Delete permanently"
                        data-testid={`email-log-delete-${row.email_log_id}`}
                      >
                        <Trash2 size={12} strokeWidth={2.2} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-2.5 py-1.5 text-[11.5px] font-mono text-navy whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    {isInbound && (
                      <span
                        aria-label="inbound reply"
                        title="Received"
                        style={{
                          display: "inline-block",
                          width: 3, height: 12, borderRadius: 1,
                          background: "#0091D5",
                        }}
                      />
                    )}
                    <span style={isReply ? { paddingLeft: 14 } : undefined}>
                      {fmtWhen(row.sent_at)}
                    </span>
                  </div>
                </td>
                <td
                  className="px-2.5 py-1.5 text-[11.5px] text-slate truncate"
                  title={row.from_email || ""}
                  data-testid={`email-log-from-${row.email_log_id}`}
                >
                  {row.from_email || <span className="italic">—</span>}
                </td>
                {/* 2026-02-27 (Dave) — Inbound rows always target the
                    Postmark inbound hash address, which is ugly and
                    adds no signal. Show a friendly label and keep the
                    real address on hover for debugging. */}
                <td className="px-2.5 py-1.5 text-[11.5px] text-navy truncate" title={row.recipient}>
                  {isInbound ? (
                    <span className="italic text-slate">uReserve inbound</span>
                  ) : row.recipient}
                </td>
                <td className="px-2.5 py-1.5 text-[11.5px] text-navy truncate" title={row.subject}>
                  {row.subject || <span className="text-slate italic">—</span>}
                </td>
                <td className="px-2.5 py-1.5">
                  <TypePill type={row.type} />
                </td>
                <td className="px-2.5 py-1.5">
                  <StatusPill status={row.status} />
                  {row.bounce_type && (
                    <span className="ml-1.5 text-[10.5px] text-coral">
                      {row.bounce_type}
                    </span>
                  )}
                </td>
                <td className="px-2.5 py-1.5 text-[11.5px] text-slate truncate" title={row.tenant_name || ""}>
                  {row.tenant_name || <span className="italic">Platform</span>}
                </td>
                <td
                  className="px-2.5 py-1.5 text-[11.5px] text-slate truncate"
                  title={row.association_name || ""}
                  data-testid={`email-log-association-${row.email_log_id}`}
                >
                  {row.association_name || <span className="italic text-slate">—</span>}
                </td>
                <td
                  className="px-2.5 py-1.5 text-[11.5px] text-slate"
                  title={row.triggered_by_email || ""}
                >
                  {row.triggered_by_name || row.triggered_by_email ? (
                    <div className="flex flex-col leading-tight">
                      <span className="text-navy font-medium truncate">
                        {row.triggered_by_name || row.triggered_by_email}
                      </span>
                      {row.triggered_by_name && row.triggered_by_email && (
                        <span className="text-[10px] text-slate truncate">
                          {row.triggered_by_email}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="italic">System</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 2026-09-13 (Dave) — Permanent-delete confirm.  We stayed
          with a lightweight inline modal (matches the rest of the
          admin tiles) rather than pulling in a shadcn AlertDialog
          for one screen. */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => !busy && setPendingDelete(null)}
          data-testid="email-log-delete-confirm"
        >
          <div
            className="bg-white rounded-[3px] shadow-xl border border-coral max-w-md w-[90%] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-coral">
              <AlertTriangle size={18} strokeWidth={2.2} />
              <span className="text-[15px] font-bold uppercase tracking-[0.06em]">
                Permanently delete?
              </span>
            </div>
            <p className="mt-3 text-[13px] text-navy leading-relaxed">
              {pendingDelete.kind === "bulk"
                ? `You are about to permanently delete ${pendingDelete.ids.length} archived ${
                    pendingDelete.ids.length === 1 ? "email" : "emails"
                  }. This cannot be undone.`
                : `You are about to permanently delete "${pendingDelete.subject}". This cannot be undone.`}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={busy}
                className="px-3 py-1 text-[12px] font-semibold border border-rule text-navy rounded-[3px] hover:bg-navy-wash transition-colors disabled:opacity-50"
                data-testid="email-log-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  runDelete(
                    pendingDelete.kind === "bulk"
                      ? pendingDelete.ids
                      : [pendingDelete.id],
                  )
                }
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1 text-[12px] font-semibold bg-coral text-white border border-coral rounded-[3px] hover:bg-[#c9552d] transition-colors disabled:opacity-50"
                data-testid="email-log-delete-confirm-button"
              >
                {busy ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Trash2 size={11} strokeWidth={2.2} />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
